/**
 * Pure chain-of-thought timeline derivation — no React / assistant-ui imports,
 * so it is unit-testable in isolation. Both the conversation CoT
 * (`agent-chain-of-thought.tsx`) and the dedup helpers consume these.
 *
 * Kept separate from `cot-timeline.tsx` (which pulls in lucide-react / markdown
 * renderers) so tests can exercise the ordering logic without a DOM/ESM chain.
 */
import { getToolDetail } from '../../utils/tool-details';
import { isSubAgentDispatchInput } from '../../utils/content-builder';
import type { LiveIterationSnapshot } from '../../hooks/useAsyncInvokeAdapter';
import type { TimelineEntry, SubAgentChild, SubAgentResult } from './cot-timeline';

/** Returns the rendered label for a tool-call entry — used for dedup
 *  comparisons across surfaces. Keeps logic in sync with `ToolCallItem`. */
export function toolCallEntryLabel(entry: Extract<TimelineEntry, { kind: 'tool-call' }>): string {
  return entry.detail ?? `Using ${entry.toolDisplayName ?? entry.toolName}...`;
}

/**
 * Builds a mini-timeline for a sub-agent from its parsed result data.
 */
export function buildSubAgentMiniTimeline(
  result: SubAgentResult | undefined,
  status: string,
  maxItems: number,
): SubAgentChild['items'] {
  if (!result) return [];
  const items: SubAgentChild['items'] = [];

  // Add reasoning as narration
  if (result.reasoningText) {
    const text = result.reasoningText.trim();
    if (text) {
      items.push({ kind: 'narration', id: `sa-narr-${items.length}`, text });
    }
  }

  // Add tool calls. Prefer the backend-authored label (e.g. "Searching …");
  // SubAgentChildRow falls back to `Using <tool>` when detail is absent.
  if (result.toolCalls) {
    for (const tc of result.toolCalls) {
      items.push({ kind: 'tool-call', id: `sa-tc-${tc.toolCallId}`, toolName: tc.toolName, detail: tc.label });
    }
  }

  // Add response summary for completed agents
  if (status === 'completed' && result.responseText) {
    const text = result.responseText.trim();
    if (text) {
      items.push({ kind: 'narration', id: `sa-resp-${items.length}`, text });
    }
  }

  return items.slice(-maxItems);
}

/**
 * Derives a flat timeline of entries from chain-of-thought parts + live snapshot.
 * Handles narration, tool calls, and sub-agent grouping.
 */
export function deriveTimelineEntries(
  parts: readonly { type: string; [key: string]: unknown }[],
  activeLiveSnapshot: LiveIterationSnapshot,
  subAgentMaxItems: number,
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  let narrationCounter = 0;
  let lastNarrationText = '';
  // Track the rendered label of the previous tool-call entry so we can
  // collapse consecutive identical tool-calls (e.g. two `Run Viewer (get)`
  // rows in a row). Reset whenever any non-tool-call entry is pushed.
  let lastToolCallLabel = '';
  // Every tool-call id seen in `parts`, INCLUDING ones collapsed by the
  // consecutive-dedup below. The live-tool-call merge gates on this so a
  // deduped part-tool-call can't be re-appended out of order (after a later
  // narration) — the "older Create under the live narration" bug.
  const seenToolCallIds = new Set<string>();
  let currentGroup: { children: Map<string, SubAgentChild>; id: string } | null = null;

  for (const part of parts) {
    if (part.type === 'reasoning') {
      const raw = String(part.text ?? '').trim();
      if (!raw) continue;
      // Deduplicate consecutive identical narration
      if (raw === lastNarrationText) continue;
      lastNarrationText = raw;
      lastToolCallLabel = '';
      entries.push({ kind: 'narration', id: `narr-${narrationCounter++}`, text: raw });
    } else if (part.type === 'tool-call') {
      const toolName = String(part.toolName ?? '');

      // The ask-user (HITL) question renders as a prominent inline card in the
      // message column, NOT as a collapsible CoT row. Skip it here so it doesn't
      // also surface as a "Using __ask_user..." one-liner.
      if (toolName === '__ask_user') {
        lastToolCallLabel = '';
        continue;
      }

      if (toolName === '__subagent_spawn') {
        // Start a new sub-agent group. Use the spawn part's toolCallId as
        // the entry id so it stays stable as new narrations get inserted
        // earlier in the timeline — otherwise AnimatePresence sees the
        // changing key and plays an exit-then-enter flicker on every
        // upstream re-derive. Falls back to entries.length if missing
        // (shouldn't happen — content-builder always sets it).
        const args = (part.args as Record<string, unknown>) ?? {};
        const agents = (args.agents as Array<{ agentId?: string; agentName?: string; runId?: string; status?: string }>) ?? [];
        const stableId = (part.toolCallId as string | undefined) ?? `sag-${entries.length}`;
        const groupId = stableId;
        const childMap = new Map<string, SubAgentChild>();
        for (const a of agents) {
          const key = a.runId || a.agentId || `pending-${childMap.size}`;
          childMap.set(key, {
            agentName: a.agentName || a.agentId || 'Sub-Agent',
            status: (a.status as SubAgentChild['status']) || 'pending',
            items: [],
          });
        }
        currentGroup = { children: childMap, id: groupId };
        entries.push({ kind: 'subagent-group', id: groupId, children: Array.from(childMap.values()) });
        lastNarrationText = '';
        lastToolCallLabel = '';
      } else if (toolName === '__subagent_run') {
        // Update current group with sub-agent details
        const args = (part.args as Record<string, unknown>) ?? {};
        const agentName = (args.agentName as string) ?? 'Sub-Agent';
        const status = (args.status as string) ?? 'pending';
        const runId = args.runId as string | undefined;

        // Sub-agent activity data is in args._progress (not part.result)
        // because assistant-ui may exclude parts with non-undefined result
        // from chainOfThought.parts.
        const parsed = (args._progress as SubAgentResult | undefined);

        const miniTimeline = buildSubAgentMiniTimeline(parsed, status, subAgentMaxItems);
        const child: SubAgentChild = {
          agentName,
          status: status as SubAgentChild['status'],
          summary: status === 'completed' && parsed?.responseText
            ? (parsed.responseText.length > 80 ? parsed.responseText.slice(0, 80) + '…' : parsed.responseText)
            : undefined,
          items: miniTimeline,
        };

        if (currentGroup) {
          const key = runId || agentName;
          currentGroup.children.set(key, child);
          // Update the group entry in-place
          const groupEntry = entries.find((e) => e.id === currentGroup!.id);
          if (groupEntry && groupEntry.kind === 'subagent-group') {
            groupEntry.children = Array.from(currentGroup.children.values());
          }
        }
        lastNarrationText = '';
        lastToolCallLabel = '';
      } else {
        // Regular tool call. Skip if this would render the exact same label
        // as the previous tool-call entry — eliminates the visual repeat the
        // user flagged ("Running Run Viewer (get)" twice in a row).
        const args = (part.args as Record<string, unknown> | undefined) ?? {};
        const toolDisplayName = part.toolDisplayName ? String(part.toolDisplayName) : undefined;
        // Prefer the backend-authored label (accurate — keyed on the real tool
        // identity). Fall back to the frontend heuristic for older runs.
        const backendLabel = typeof part.label === 'string' && part.label ? part.label : undefined;
        const detail = backendLabel ?? getToolDetail(toolName, args) ?? undefined;
        const label = toolCallEntryLabel({ kind: 'tool-call', id: '', toolName, toolDisplayName, detail });
        const id = `tc-${String(part.toolCallId ?? narrationCounter++)}`;
        // Record the id even when collapsed below, so the live merge can't
        // resurrect this call after a later narration.
        seenToolCallIds.add(id);
        if (label === lastToolCallLabel) {
          continue;
        }
        entries.push({
          kind: 'tool-call',
          id,
          toolName,
          toolDisplayName,
          detail,
        });
        lastNarrationText = '';
        lastToolCallLabel = label;
      }
    }
  }

  // Check if sub-agents are still running → add waiting entry
  if (currentGroup) {
    const children = Array.from(currentGroup.children.values());
    const anyRunning = children.some((c) => c.status === 'running' || c.status === 'pending');
    if (anyRunning) {
      entries.push({ kind: 'waiting', id: `wait-${currentGroup.id}` });
    }
  }

  // Merge live tool calls not yet in parts. Apply the same consecutive
  // dedup we apply above so a live-only tool-call doesn't duplicate the
  // last entry's label.
  const existingTcIds = new Set<string>(seenToolCallIds);
  for (const e of entries) {
    if (e.kind === 'tool-call') existingTcIds.add(e.id);
  }
  const tailLabel = (() => {
    const last = entries[entries.length - 1];
    return last && last.kind === 'tool-call' ? toolCallEntryLabel(last) : '';
  })();
  let runningTailLabel = tailLabel;
  for (const iter of activeLiveSnapshot.iterations) {
    for (const tc of iter.toolCalls) {
      const id = `tc-${tc.toolCallId}`;
      if (existingTcIds.has(id) || tc.toolName === '__ask_user' || tc.toolName.startsWith('__subagent_') || isSubAgentDispatchInput(tc.input)) continue;
      const detail = tc.label ?? (tc.input ? getToolDetail(tc.toolName, tc.input) ?? undefined : undefined);
      const label = toolCallEntryLabel({ kind: 'tool-call', id: '', toolName: tc.toolName, toolDisplayName: tc.toolDisplayName, detail });
      if (label === runningTailLabel) continue;
      entries.push({
        kind: 'tool-call',
        id,
        toolName: tc.toolName,
        toolDisplayName: tc.toolDisplayName,
        detail,
      });
      existingTcIds.add(id);
      runningTailLabel = label;
    }
  }

  return entries;
}
